import React, { FC, PropsWithChildren } from 'react';
import { useTranslation } from 'next-i18next';
import { BlockedTime } from 'src/common/providers/useBookingMotivators';
import { convertTo12HourFormat } from 'src/listing/helper';
import useDateFnsLocale from 'src/common/hooks/useDateFnsLocale';
import type { Motivator } from 'src/common/types/api/v4/bookingMotivators';
import getClassNames from 'src/common/utils/getClassNames';
import classNames from 'classnames';
import { BOOKING_MOTIVATOR_STATES } from '../../constants';
import Typography from '../Typography';
import IconDS22 from '../IconDS22';
import LoadingSpinner from '../LoadingSpinner';
import IconPictogram, { PictogramIds, Sizes } from '../IconPictogram';
import styles from './BookingMotivator.module.scss';

const DEFAULT_BLOCK_TIME_COUNT = 3;

export const {
  UNAVAILABLE,
  LIMITED_AVAILABILITY,
  IN_DEMAND,
  HIGH_DEMAND,
  UNUSUAL_SIGHTING,
  LOOKS_GOOD,
  INSTABOOK,
  INSTABOOK_EXPIRING,
  FREE_CANCELLATION,
  OFF_SEASON,
  NO_SAME_DAY_PRICING,
} = BOOKING_MOTIVATOR_STATES;

export type BookingMotivatorProps = {
  motivators?: Motivator[];
  /** Viewing calendar as multi-day, this will be 2+, which affects LIMITED_AVAILABILITY plural */
  dateCount?: number;
  /** Only exists for LIMITED_AVAILABILITY motivator */
  blockedTimes?: BlockedTime[];
  blockedTimesLoading?: boolean;
  /** Only exists for INSTABOOK_EXPIRING motivator */
  daysUntilInstabook?: number;
  /** showing count number of blocked times  */
  visibleBlockedTimeCount?: number;
  showBlockTimeDescription?: boolean;
  /** Compact will hide all icons and make text show inline */
  variant?: 'compact';
  anticipatedDays?: number;
  size?: Sizes;
  hardEdge?: boolean;
};

type MotivatorItemProps = {
  /** Undefined will use the default IconDS22 'calendar-day' and default-description */
  motivator?: Motivator;
} & Pick<BookingMotivatorProps,
'blockedTimes'
| 'dateCount'
| 'daysUntilInstabook'
| 'visibleBlockedTimeCount'
| 'showBlockTimeDescription'
| 'variant'
| 'anticipatedDays'
| 'size'>;

const iconMap: Record<Motivator, PictogramIds> = {
  [UNAVAILABLE]: 'unavailable',
  [OFF_SEASON]: 'unavailable',
  [NO_SAME_DAY_PRICING]: 'unavailable',
  [LIMITED_AVAILABILITY]: 'limited-availability',
  [IN_DEMAND]: 'in-demand',
  [HIGH_DEMAND]: 'high-demand',
  [UNUSUAL_SIGHTING]: 'rare-discovery',
  [LOOKS_GOOD]: 'looks-good',
  [INSTABOOK]: 'instant-booking',
  [INSTABOOK_EXPIRING]: 'instant-booking',
} as const;

type BlockTimesWrapperProps = Pick<MotivatorItemProps, 'variant'> & PropsWithChildren;

const BlockTimesWrapper: FC<BlockTimesWrapperProps> = ({ variant, children }) => (
  variant === 'compact'
    ? children
    : <div className={styles.blockedTimes}>{children}</div>
);

const MotivatorItem: FC<MotivatorItemProps> = ({
  motivator,
  dateCount = 1,
  blockedTimes = [],
  daysUntilInstabook = 1,
  visibleBlockedTimeCount = DEFAULT_BLOCK_TIME_COUNT,
  showBlockTimeDescription = true,
  size,
  variant,
  anticipatedDays,
}) => {
  const [t] = useTranslation('common');
  const locale = useDateFnsLocale();

  const motivatorKebabCase = motivator?.replace('_', '-').toLowerCase();
  const isMultiDay = dateCount > 1;
  const isCompact = variant === 'compact';

  const context = isMultiDay ? 'multiday' : undefined;
  const count = motivator === INSTABOOK_EXPIRING ? daysUntilInstabook : dateCount;

  let description = motivator
    ? t(`booking-motivator--${motivatorKebabCase}--description`, { count, context, days: anticipatedDays })
    : t('booking-motivator--select-date--title');

  let title = (motivator && motivator !== 'INSTABOOK')
    ? t(`booking-motivator--${motivatorKebabCase}--title`, { context })
    : '';

  if (motivator === IN_DEMAND) {
    title = t('booking-motivator--in-demand--heading', { count });
    description = t('booking-motivator--in-demand--text');
  }

  const showBlockedTimes = motivator === 'LIMITED_AVAILABILITY' && blockedTimes.length > 0 && !isMultiDay;
  const showDescription = showBlockTimeDescription
  && blockedTimes.length > DEFAULT_BLOCK_TIME_COUNT;

  const variantStyle = { 'variant-compact': isCompact };

  return (
    <li className={getClassNames(styles, 'motivator', classNames(variantStyle))}>
      <div className={styles.iconWrapper}>
        {motivator ? (
          <IconPictogram
            id={iconMap[motivator]}
            className={styles.icon}
            size={size}
          />
        ) : (
          <IconDS22
            id="calendar-day"
            className={styles.icon}
            size={size}
          />
        )}
      </div>
      <div className={getClassNames(styles, 'content', classNames(variantStyle))}>
        {title && (
          <Typography type={isCompact ? 'span' : 'h4'} size="body-s" sizeFromBreakpointM={isCompact ? undefined : 'body-l'} weight="semibold">{title}</Typography>
        )}
        {description && (
          <Typography type={isCompact ? 'span' : 'p'} size="body-s" sizeFromBreakpointM={isCompact ? undefined : 'body-m'}>{description}</Typography>
        )}
        {showBlockedTimes && (
          <BlockTimesWrapper variant={variant}>
            {blockedTimes.slice(0, visibleBlockedTimeCount).map((time) => (
              <Typography
                key={`${time.start_time}-${time.end_time}`}
                type="span"
                size="body-s"
              >
                {t('booking-motivator--limited-availability-times', {
                  startTime: convertTo12HourFormat(time.start_time, locale),
                  endTime: convertTo12HourFormat(time.end_time, locale),
                })}
              </Typography>
            ))}
            {showDescription && (
              <Typography
                type="span"
                size="body-s"
              >
                {t('booking-motivator--limited-availability-times-unavailable')}
              </Typography>
            )}
          </BlockTimesWrapper>
        )}
      </div>
    </li>
  );
};

const BookingMotivator: FC<BookingMotivatorProps> = ({
  motivators = [],
  dateCount,
  blockedTimes = [],
  blockedTimesLoading,
  daysUntilInstabook,
  visibleBlockedTimeCount = DEFAULT_BLOCK_TIME_COUNT,
  showBlockTimeDescription = true,
  variant,
  size,
  anticipatedDays,
  hardEdge,
}) => {
  const hasLimitedAvailability = motivators.includes(LIMITED_AVAILABILITY);
  if (blockedTimesLoading && hasLimitedAvailability) {
    return <div className={styles.loading}><LoadingSpinner variant="s" /></div>;
  }

  return (
    <ul className={getClassNames(styles, 'root', classNames({ hardEdge }))}>
      {motivators.length > 0
        ? motivators.map((motivator) => (
          <MotivatorItem
            motivator={motivator}
            dateCount={dateCount}
            blockedTimes={blockedTimes}
            daysUntilInstabook={daysUntilInstabook}
            visibleBlockedTimeCount={visibleBlockedTimeCount}
            showBlockTimeDescription={showBlockTimeDescription}
            variant={variant}
            size={size ?? 'm'}
            anticipatedDays={anticipatedDays}
            key={motivator}
          />
        )) : (
          <MotivatorItem
            size={size ?? 'm'}
          />
        )}
    </ul>
  );
};

export default BookingMotivator;
